/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectCommandRun } from 'modules/dashboard/selectors';
import { UiController } from '..';
import { PIPELINE_ACTION, nextPipelineAction } from '../utils/pipeline';

/**
 * Custom hook to apply command values one after another, in the order of the step list.
 * Each step listens to its event, waits until it is ready and is applied once.
 * Commands that are not in the command url are skipped, see nextPipelineAction for a step.
 * @param {Array} steps - ordered step list, should be a constant: { command, event, ready, apply }
 * A step without an event reads its value from the values kept on the command run
 * A step can also have
 *   prepare    - (value, context) => void, called once when the step is reached and is not ready
 *   start      - (value, context, run) => void, called once when the step is ready, before delayBefore
 *   delayBefore - milliseconds to wait once the step is ready before it is applied
 *   delayAfter - milliseconds to wait after the step is applied before the next step,
 *                a number or (value) => number
 * @param {Object} context - current state the steps read
 * @param {Boolean} enabled - whether steps can be applied now
 * @param {Function} onDone - called once when every command of the command url is applied
 */
const useCommandPipeline = (steps, context, { enabled = true, onDone } = {}) => {
  const commandRun = useSelector(selectCommandRun);
  // command values that have arrived and are not applied yet
  const pendingRef = useRef({});
  // command url execution that is being applied
  const runRef = useRef({ id: null, keys: [], remaining: [], taken: [], isDone: true });
  const [, refresh] = useReducer((count) => count + 1, 0);
  // step that prepare is already called for, as run id and command
  const preparedRef = useRef('');
  // step that start is already called for, as run id and command
  const startedRef = useRef('');
  // step that delayBefore is already waited for, as run id and command
  const delayedRef = useRef('');
  // timer of a step delay, no step is applied while it is running
  const delayTimerRef = useRef(null);

  // Add listener functions to keep the command value of each step
  useEffect(() => {
    const listeners = steps
      .filter((step) => step.event)
      .map((step) => {
        const listener = (value) => {
          pendingRef.current[step.command] = value;
          refresh();
        };
        UiController.subscribe(step.event, listener);
        return [step.event, listener];
      });
    return () => {
      listeners.forEach(([event, listener]) => UiController.unsubscribe(event, listener));
    };
  }, [steps]);

  // Clear the step delay timer when the component unmounts
  useEffect(() => () => clearTimeout(delayTimerRef.current), []);

  // Triggers on every render, applies one step at a time so the next step sees the updated state
  useEffect(() => {
    if (commandRun && commandRun.id !== runRef.current.id) {
      // drop values left from a previous command url that are not part of this one
      Object.keys(pendingRef.current).forEach((key) => {
        if (!commandRun.keys.includes(key)) delete pendingRef.current[key];
      });
      runRef.current = {
        id: commandRun.id,
        keys: commandRun.keys,
        remaining: [...commandRun.keys],
        taken: [],
        isDone: false,
      };
    }
    // take the values kept on the command run for the steps that have no event
    steps.forEach((step) => {
      const values = commandRun?.values ?? {};
      if (
        !step.event &&
        Object.hasOwn(values, step.command) &&
        !runRef.current.taken.includes(step.command)
      ) {
        runRef.current.taken.push(step.command);
        pendingRef.current[step.command] = values[step.command];
      }
    });
    if (!enabled) return;
    if (delayTimerRef.current !== null) return;
    const run = runRef.current;
    const pending = pendingRef.current;
    const action = nextPipelineAction(steps, pending, run.remaining, context, {
      keys: run.keys,
      pending,
    });
    if (action.type === PIPELINE_ACTION.APPLY) {
      const { step } = action;
      const stepId = `${run.id}:${step.command}`;
      if (step.start && startedRef.current !== stepId) {
        startedRef.current = stepId;
        step.start(pending[step.command], context, { keys: run.keys, pending });
      }
      if (step.delayBefore > 0 && delayedRef.current !== stepId) {
        delayedRef.current = stepId;
        delayTimerRef.current = setTimeout(() => {
          delayTimerRef.current = null;
          refresh();
        }, step.delayBefore);
        return;
      }
      const consumed = step.apply(pending[step.command], context, { keys: run.keys, pending }) ?? [
        step.command,
      ];
      const delayAfter =
        typeof step.delayAfter === 'function'
          ? step.delayAfter(pending[step.command])
          : step.delayAfter;
      consumed.forEach((key) => delete pending[key]);
      run.remaining = run.remaining.filter((key) => !consumed.includes(key));
      if (delayAfter > 0) {
        delayTimerRef.current = setTimeout(() => {
          delayTimerRef.current = null;
          refresh();
        }, delayAfter);
      }
      refresh();
    } else if (action.type === PIPELINE_ACTION.WAIT) {
      const { step } = action;
      // prepare the step once every command it reads has arrived
      const isMissing = [step.command, ...(step.requires ?? [])].some(
        (key) => run.remaining.includes(key) && !Object.hasOwn(pending, key)
      );
      const stepId = `${run.id}:${step.command}`;
      if (!isMissing && step.prepare && preparedRef.current !== stepId) {
        preparedRef.current = stepId;
        step.prepare(pending[step.command], context);
        refresh();
      }
    } else if (action.type === PIPELINE_ACTION.DONE && !run.isDone) {
      run.isDone = true;
      onDone?.();
    }
  });
};

export default useCommandPipeline;
